import React from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@pv/common/hooks';
import { PvDialog } from '@pv/common/components';
import { enqueueSnackbar } from 'notistack';

import { FileUploader } from './FileUploader';
import { TextField } from '@mui/material';

interface VenueDataImport {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  errorMessages: string[];
  stagingResults?: any;
  storageUrl?: string | null;
}
interface VenueDataImportFormProps {
  venueId: string;
  show: boolean;
  onClose: () => void;
  onCompleted: (venueDataImport: VenueDataImport) => void;
}

const createVenueDataImportMutation = gql`
  mutation CreateVenueDataImport($input: CreateVenueDataImportInput!) {
    createVenueDataImport(input: $input) {
      venueDataImport {
        id
        createdAt
        updatedAt
        status
        errorMessages
      }
      errors {
        message
      }
    }
  }
`;

export const TripleseatImportForm = ({
  venueId,
  show,
  onClose,
  onCompleted,
}: VenueDataImportFormProps) => {
  const [eventsCsv, setEventsCsv] = React.useState<any>(null);
  const [eventPaymentsCsv, setEventPaymentsCsv] = React.useState<any>(null);
  const [accountsCsv, setAccountsCsv] = React.useState<any>(null);
  const [leadsCsv, setLeadsCsv] = React.useState<any>(null);
  const [beosZip, setBeosZip] = React.useState<any>(null);
  const [dashboardEventsCsv, setDashboardEventsCsv] = React.useState<any>(null);
  const [dashboardLeadsCsv, setDashboardLeadsCsv] = React.useState<any>(null);
  const [accountsReportCsv, setAccountsReportCsv] = React.useState<any>(null);
  const [locationName, setLocationName] = React.useState<string>('');

  const [createVenueDataImport, { loading }] = useMutation(
    createVenueDataImportMutation,
    {
      onNoErrorsCompleted: (response) => {
        const newVenueDataImport =
          response.createVenueDataImport.venueDataImport;
        onCompleted(newVenueDataImport);
        enqueueSnackbar('Data Import Created', {
          variant: 'success',
        });
        onClose();
      },
      onSomeErrorsCompleted: (resp) => {
        const errors = resp.createVenueDataImport.errors;
        errors.forEach((error: { message: string }) => {
          enqueueSnackbar(`Error creating data import: ${error.message}`, {
            variant: 'error',
          });
        });
      },
    }
  );

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const variables = {
      input: {
        venueId: venueId,
        eventsCsv,
        eventPaymentsCsv,
        accountsCsv,
        leadsCsv,
        beosZip,
        dashboardEventsCsv,
        dashboardLeadsCsv,
        accountsReportCsv,
        locationName: locationName.length > 0 ? locationName : null,
      },
    };
    createVenueDataImport({
      variables: variables,
    });
  };

  return (
    <PvDialog
      open={show}
      onClose={onClose}
      title="Create New Tripleseat Import"
      confirmButtonText="Import Data"
      confirmButtonColor="primary"
      onConfirm={(e: any) => handleSubmit(e)}
      loading={loading}
    >
      <div>
        <form onSubmit={handleSubmit}>
          <h3>Import Tripleseat Data</h3>
          <p>Data exported from the Tripleseat dashboard</p>

          <FileUploader
            label="Accounts Report"
            fileName="accountsReportCsv"
            fileType="csv"
            file={accountsReportCsv}
            color={'primary.pink'}
            onFileAttached={(file) => setAccountsReportCsv(file)}
          />
          <FileUploader
            label="Leads"
            fileName="dashboardLeadsCsv"
            fileType="csv"
            file={dashboardLeadsCsv}
            color={'#FFEBA3'}
            onFileAttached={(file) => setDashboardLeadsCsv(file)}
          />
          <FileUploader
            label="Event Details Report"
            fileName="dashboardEventsCsv"
            fileType="csv"
            file={dashboardEventsCsv}
            color={'#89D6F0'}
            onFileAttached={(file) => setDashboardEventsCsv(file)}
          />
          <FileUploader
            label="Event Payments Report"
            fileName="eventPaymentsCsv"
            fileType="csv"
            file={eventPaymentsCsv}
            color={'#B1F3D9'}
            onFileAttached={(file) => setEventPaymentsCsv(file)}
          />
          <FileUploader
            label="Documents Zipfile"
            fileName="beosZip"
            fileType="zip"
            file={beosZip}
            color={'primary.hoverNavy'}
            onFileAttached={(file) => setBeosZip(file)}
          />
          <h3>Import Tripleseat API Data</h3>
          <p>Data exported from the Tripleseat API</p>
          <FileUploader
            label="Tripleseat Accounts CSV"
            fileName="accountsCsv"
            fileType="csv"
            file={accountsCsv}
            color={'primary.pink'}
            onFileAttached={(file) => setAccountsCsv(file)}
          />
          <FileUploader
            label="Tripleseat Leads CSV"
            fileName="leadsCsv"
            fileType="csv"
            file={leadsCsv}
            color={'#FFEBA3'}
            onFileAttached={(file) => setLeadsCsv(file)}
          />
          <FileUploader
            label="Tripleseat Events CSV"
            fileName="eventsCsv"
            fileType="csv"
            file={eventsCsv}
            color={'#89D6F0'}
            onFileAttached={(file) => setEventsCsv(file)}
          />
          <h3>Change Location Name</h3>
          <p>
            Does the Tripleseat location have a different name than the venue?
            If so, enter the exact name of the Tripleseat location here.
          </p>
          <TextField
            label="Location Name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
          />
        </form>
      </div>
    </PvDialog>
  );
};
